import React, { useEffect, useState } from "react";
import ImageSlider, { photoList } from "@/components/ImageSlider";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageCircle, Share2, Briefcase, Users, UserSearch, Sparkles, Store, MapPin, ShieldAlert } from "lucide-react";
import { fetchPublicActivityProfile, resolveImageUrl } from "@/lib/api";
import MapPreview from "@/components/maps/MapPreview";
import { ShareLocationCard } from "@/components/maps/LocationShare";
import { useApp } from "@/context/AppContext";
import { waUrl } from "@/lib/whatsapp";
import { contactGate } from "@/lib/contactGate";
import { serviceEnquiryMsg, jobEnquiryMsg, otherEnquiryMsg } from "@/lib/enquiry";
import { HireCard } from "@/components/Cards";
import BackButton from "@/components/BackButton";

const digits = (n) => { const d = String(n || "").replace(/\D/g, ""); return d.length === 10 ? `91${d}` : d; };

function Actions({ phone, whatsapp, msg, testid, locked }) {
  const { user } = useApp();
  const gate = (e) => { if (contactGate(user, locked)) e.preventDefault(); };
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {(phone || locked) && <a href={phone ? `tel:${phone}` : undefined} onClick={gate} role="button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 px-4 py-2 text-sm font-semibold" data-testid={`${testid}-call`}><Phone className="w-4 h-4" /> Call</a>}
      {(whatsapp || phone || locked) && <a href={whatsapp || phone ? waUrl(digits(whatsapp || phone), msg) : undefined} onClick={gate} role="button" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white px-4 py-2 text-sm font-semibold" data-testid={`${testid}-whatsapp`}><MessageCircle className="w-4 h-4" /> WhatsApp Enquiry</a>}
    </div>
  );
}

const Card = ({ icon: Icon, title, children, testid }) => (
  <section className="bg-white border border-slate-100 rounded-2xl p-5" data-testid={testid}>
    <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-2"><Icon className="w-5 h-5 text-emerald-700" /> {title}</h2>
    {children}
  </section>
);
const Row = ({ label, value }) => (value ? <div className="text-sm text-slate-700"><span className="text-slate-400">{label}: </span>{value}</div> : null);

export default function PublicActivityProfile() {
  const { user } = useApp();
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setP(null); setErr(false); fetchPublicActivityProfile(id).then(setP).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="public-activity-missing">This profile is not public. <Link to="/" className="text-emerald-700 font-semibold">Go home</Link></div>;
  if (!p) return <div className="py-16 text-center text-slate-500">Loading...</div>;
  const { professional: pro, employer: emp, job_seeker: js, other: oth } = p.sections;
  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) await navigator.share({ title: p.name, url }); else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); } } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share — please copy the link from the address bar"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-4" data-testid="public-activity-profile">
      <BackButton fallback="/category/services" />
      <div className="flex items-center gap-4">
        <img src={resolveImageUrl(pro?.photo || p.avatar_url) || ""} alt="" className="w-16 h-16 rounded-2xl object-cover bg-slate-100" />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 truncate" data-testid="public-activity-name">{pro?.name || emp?.org_name || oth?.activity_name || js?.name || p.name}</h1>
          {pro?.profession && <div className="text-sm text-emerald-700 font-semibold">{pro.profession}</div>}
        </div>
        <button onClick={share} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 px-3 py-2 text-sm font-semibold" data-testid="public-activity-share"><Share2 className="w-4 h-4" /> Share</button>
      </div>

      {p.shop && <Card icon={Store} title="Shop" testid="pa-shop"><Link to={`/v/${p.shop.slug || p.shop.shop_id}`} className="text-emerald-700 font-semibold text-sm">{p.shop.name || "Visit shop"} →</Link></Card>}

      {pro && (
        <Card icon={Briefcase} title="Services" testid="pa-professional">
          <p className="text-sm text-slate-700 whitespace-pre-line">{pro.description}</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-1.5">
            <Row label="Specialties" value={pro.specialties} />
            <Row label="Fee" value={pro.fee_mode === "fixed" && pro.fee != null ? `₹${pro.fee} ${pro.pricing_unit || ""}` : "Fee as discussed / custom quotation"} />
            <Row label="Availability" value={pro.availability} />
            <Row label="Appointments" value={pro.appointments ? "Available on request" : ""} />
            <Row label="Service area" value={pro.service_area} />
            <Row label="Location" value={[pro.address, pro.locality, pro.pincode].filter(Boolean).join(", ")} />
            <Row label="Qualification (self-declared)" value={pro.qualification} />
          </div>
          {pro.qualification && <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 rounded-full px-2 py-0.5" data-testid="pa-not-verified"><ShieldAlert className="w-3 h-3" /> Credentials not verified by VerB</div>}
          {(pro.gallery || []).length > 0 && <ImageSlider images={pro.gallery} alt="" className="mt-3 h-56 sm:h-72 rounded-2xl bg-slate-100" imgClassName="object-contain" testid="pa-pro-gallery-slider" />}
          <Actions phone={pro.phone} whatsapp={pro.whatsapp} locked={pro.contact_locked} msg={serviceEnquiryMsg(pro.name, pro.profession)} testid="pa-pro" />
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <MapPreview embedUrl={pro.map_embed_url} viewUrl={pro.map_view_url} directionsUrl={pro.directions_url} approx={!!pro.map_approx} noDirectionsText="Provider ne exact location share nahi ki — directions ke liye Call/WhatsApp karein." emptyText="Provider ki exact map location saved nahi hai — sahi pata Call/WhatsApp karke poochein." testid="pa-pro-map" />
            <ShareLocationCard serviceId={`pro-${p.id}`} isOwner={user?.id === p.id} />
          </div>
        </Card>
      )}

      {emp && (
        <Card icon={Users} title="Employer" testid="pa-employer">
          <div className="font-semibold text-slate-900">{emp.org_name}</div>
          {emp.about && <p className="text-sm text-slate-700 mt-1">{emp.about}</p>}
          <Row label="Address" value={[emp.address, emp.pincode].filter(Boolean).join(", ")} />
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-2">Active vacancies</div>
          {(emp.jobs || []).length === 0 ? <div className="text-sm text-slate-400">No active vacancies right now.</div> : <div className="grid sm:grid-cols-2 gap-3">{emp.jobs.map((j) => <HireCard key={j.id} job={j} />)}</div>}
          <Actions phone={emp.phone} whatsapp={emp.whatsapp} locked={emp.contact_locked} msg={jobEnquiryMsg(emp.org_name, "")} testid="pa-emp" />
        </Card>
      )}

      {js && (
        <Card icon={UserSearch} title="Looking for work" testid="pa-job-seeker">
          <Row label="Skills" value={js.skills} />
          <Row label="Qualifications" value={js.qualifications} />
          <Row label="Experience" value={js.experience} />
          <Row label="Preferred work" value={js.preferred_work} />
          {js.preferred_pincode && <div className="text-sm text-slate-700 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Preferred PIN {js.preferred_pincode}</div>}
          <Actions phone={js.phone} whatsapp={js.contact_pref === "whatsapp" ? js.phone : ""} locked={js.contact_locked} msg={`Namaste ${js.name || ""}, VerB par aapki profile dekhi. Ek kaam ke baare mein baat karni hai.`} testid="pa-js" />
        </Card>
      )}

      {oth && (
        <Card icon={Sparkles} title={oth.activity_type || "Activity"} testid="pa-other">
          <div className="font-semibold text-slate-900">{oth.activity_name}</div>
          <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{oth.description}</p>
          <Row label="Pricing" value={oth.pricing} />
          <Row label="PIN" value={oth.pincode} />
          {(oth.photos || []).length > 0 && <ImageSlider images={oth.photos} alt="" className="mt-3 h-56 sm:h-72 rounded-2xl bg-slate-100" imgClassName="object-contain" testid="pa-other-photos-slider" />}
          <Actions phone={oth.phone} whatsapp={oth.whatsapp} locked={oth.contact_locked} msg={otherEnquiryMsg(oth.activity_name)} testid="pa-other" />
        </Card>
      )}
    </div>
  );
}
